import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement} [slidesContainer]
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /** @type {1|-1} */
  #direction = 1;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    const length = (this.refs.slides ?? []).length;
    if (length <= 1) return;

    let relativeIndex = this.#current % length;
    if (relativeIndex < 0) relativeIndex += length;

    if (relativeIndex === length - 1) this.#direction = -1;
    else if (relativeIndex === 0) this.#direction = 1;

    this.current += this.#direction;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];
    const length = slides.length;

    const prevCurrent = this.#current;
    this.#current = current;

    let newIndex = current % length;
    if (newIndex < 0) newIndex += length;

    let oldIndex = prevCurrent % length;
    if (oldIndex < 0) oldIndex += length;

    const slidesContainer =
      /** @type {HTMLElement|null} */ (this.refs.slidesContainer ?? this.querySelector('.announcement-bar__slides'));
    const isSlide = slidesContainer?.dataset.transition === 'slide';

    const enteringSlide = /** @type {HTMLElement|undefined} */ (slides[newIndex]);
    const leavingSlide = /** @type {HTMLElement|undefined} */ (slides[oldIndex]);

    if (isSlide && newIndex !== oldIndex && length > 0 && enteringSlide && leavingSlide) {
      const goingForward = current > prevCurrent;
      const enterFrom = goingForward ? '100%' : '-100%';
      const exitTo = goingForward ? '-100%' : '100%';

      // Instantly place the entering slide off-screen (no transition)
      enteringSlide.style.transition = 'none';
      enteringSlide.style.transform = `translateY(${enterFrom})`;
      enteringSlide.setAttribute('aria-hidden', 'false');

      // Force reflow so the browser registers the starting position
      enteringSlide.offsetHeight;

      // Re-enable CSS transition and animate both into their target positions
      enteringSlide.style.transition = '';
      enteringSlide.style.transform = 'translateY(0)';
      leavingSlide.style.transform = `translateY(${exitTo})`;

      // After the leaving slide finishes, hide it and clean up inline styles
      leavingSlide.addEventListener(
        'transitionend',
        () => {
          leavingSlide.setAttribute('aria-hidden', 'true');
          leavingSlide.style.transform = '';
          enteringSlide.style.transform = '';
        },
        { once: true }
      );

      // Keep all other slides hidden and reset
      slides.forEach((slide, index) => {
        if (index !== newIndex && index !== oldIndex) {
          slide.setAttribute('aria-hidden', 'true');
          slide.style.transform = '';
          slide.style.transition = '';
        }
      });
    } else {
      slides.forEach((slide, index) => {
        slide.setAttribute('aria-hidden', `${index !== newIndex}`);
      });
    }
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
